import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getProductImageUrl } from '../../../utils/productImage';
import { hasDisplayPrice } from '../../../utils/priceDisplay';

const RelatedProducts = ({ products }) => {
  const navigate = useNavigate();
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
  };

  return (
    <div className="bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
            Related Products
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Discover complementary medical devices that work perfectly with your selected product
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products?.map((product, index) => (
            <motion.div
              key={product?.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-medical-hover transition-all duration-300 group"
            >
              {/* Product Image (no fallback) */}
              <div className="aspect-square bg-muted relative overflow-hidden flex items-center justify-center">
                {getProductImageUrl(product?.image) ? (
                  <Image
                    src={getProductImageUrl(product?.image)}
                    alt={product?.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-text-secondary text-xs">No image</span>
                )}
                {product?.isNew && (
                  <div className="absolute top-3 left-3 px-2 py-1 bg-success text-white text-xs font-medium rounded-full">
                    New
                  </div>
                )}
                {product?.discount && (
                  <div className="absolute top-3 right-3 px-2 py-1 bg-error text-white text-xs font-medium rounded-full">
                    -{product?.discount}%
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-4">
                <div className="mb-2">
                  <span className="text-xs text-primary font-medium">{product?.category}</span>
                </div>
                <h3 className="font-semibold text-text-primary mb-2 line-clamp-2">
                  {product?.name}
                </h3>
                <p className="text-sm text-text-secondary mb-3 line-clamp-2">
                  {product?.description}
                </p>

                {/* Certifications */}
                <div className="flex items-center gap-1 mb-3">
                  {product?.certifications?.slice(0, 2)?.map((cert, certIndex) => (
                    <span
                      key={certIndex}
                      className="inline-flex items-center px-1.5 py-0.5 bg-success/10 text-success text-xs rounded"
                    >
                      <Icon name="Shield" size={10} className="mr-1" />
                      {cert}
                    </span>
                  ))}
                  {product?.certifications?.length > 2 && (
                    <span className="text-xs text-text-secondary">
                      +{product?.certifications?.length - 2} more
                    </span>
                  )}
                </div>

                {/* Price and Action */}
                <div className="flex items-center justify-between">
                  <div>
                    {hasDisplayPrice(product?.price) && (
                      <div className="text-lg font-bold text-text-primary">
                        {formatPrice(product.price)}
                      </div>
                    )}
                    {hasDisplayPrice(product?.originalPrice ?? product?.original_price) && (
                      <div className="text-sm text-text-secondary line-through">
                        {formatPrice(product?.originalPrice ?? product?.original_price)}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={() => {
                      const idOrSlug = product?.slug || product?.id;
                      if (!idOrSlug) return;
                      navigate(`/product-detail/${idOrSlug}`);
                    }}
                  >
                    View
                  </Button>
                </div>

                {/* Availability */}
                <div className="mt-3 pt-3 border-t border-border">
                  <div className={`inline-flex items-center text-xs ${
                    product?.availability === 'In Stock' ?'text-success' 
                      : product?.availability === 'Pre-Order' ?'text-warning' :'text-error'
                  }`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      product?.availability === 'In Stock' ?'bg-success' 
                        : product?.availability === 'Pre-Order' ?'bg-warning' :'bg-error'
                    }`} />
                    {product?.availability}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-8">
          <Button
            variant="outline"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => navigate('/product-portfolio')}
          >
            View All Products
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RelatedProducts;